import type {RefCallback} from 'react'

import columnStyles from '../frames/BoardColumnFrame.module.css'
import rowStyles from '../frames/BoardRowFrame.module.css'
import styles from './BoardWindow.module.css'

export type BoardWindowSpacerProps = {
  /** Pixels to reserve, or `null` when this end of the axis has nothing left out. */
  size: number | null
  /** Which way the axis runs: `inline` across the columns, `block` down the rows. */
  axis: 'inline' | 'block'
}

/**
 * The part of an axis that is not rendered, as a box of exactly its size.
 *
 * It is what makes the scrollbars describe the whole board rather than the part of it
 * that happens to be on screen, and it is a box in the ordinary flow rather than an
 * offset written onto the items, which is the choice everything else depends on. Offset
 * items would need a container as tall as every row for the rows to be positioned inside,
 * and a row asking for `100%` of that container would get the height of the whole board
 * instead of one band — the geometry the board's `minmax(0, 1fr)` track already exists to
 * avoid. Left in flow, a row resolves its height against exactly the box it always did.
 */
export function BoardWindowSpacer({size, axis}: BoardWindowSpacerProps) {
  if (size === null) return null

  return (
    <div
      aria-hidden="true"
      className={styles.Spacer}
      style={axis === 'inline' ? {inlineSize: `${size}px`} : {blockSize: `${size}px`}}
    />
  )
}

export type BoardCardSpacerProps = {
  /** Pixels to reserve. */
  size: number
}

/**
 * The cards a cell's window left out, as a list item of exactly their height.
 *
 * A list item, because it sits between the cards inside the cell's `<ul>`, and a `<ul>`
 * whose children are not all `<li>` is not a list any more -- which would cost a reader the
 * position and the size each card is already having to state by hand. It carries nothing
 * and is hidden from assistive technology, so the list is still exactly as long as the
 * cards in it.
 *
 * **It must never be given content or a focusable descendant.** `aria-hidden` on an element
 * holding either is a way of hiding real things from a reader, and it is only safe here
 * because there is nothing inside to hide. `role="presentation"` is deliberately not added
 * as well: it would be redundant, and this repository forbids putting it on a semantic
 * element.
 */
export function BoardCardSpacer({size}: BoardCardSpacerProps) {
  return <li aria-hidden="true" className={styles.Spacer} style={{blockSize: `${size}px`}} />
}

export type BoardMetricsProbesProps = {
  columnRef: RefCallback<HTMLElement>
  rowRef: RefCallback<HTMLElement>
}

/**
 * Where the board's two stated lengths are resolved to pixels, for `useBoardMetrics` to
 * read.
 *
 * Each probe wears the class of the frame it stands in for rather than restating that
 * frame's length, so the two cannot drift apart and neither can disagree about which edges
 * of the box the length describes.
 *
 * They are empty, and that is the point: a probe has no contents for its size to be
 * measured from, so what comes back is the length the board stated and nothing else.
 *
 * They belong inside the strip of rows because a row's height may be a percentage, and a
 * percentage resolves against whatever contains it. The wrapper fills that strip exactly,
 * so the row probe reads `100%` of the same box a real row does.
 */
export function BoardMetricsProbes({columnRef, rowRef}: BoardMetricsProbesProps) {
  return (
    <div aria-hidden="true" className={styles.Metrics}>
      <div ref={columnRef} className={columnStyles.ColumnFrame} />
      <div ref={rowRef} className={rowStyles.RowFrame} />
    </div>
  )
}
